import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Facebook, Instagram, Linkedin, Youtube, MessageCircle, Mail } from 'lucide-react';

const Footer = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      about: "About",
      company: "Company",
      aboutUs: "About Us",
      meetTeam: "Meet The Team",
      services: "Our Services",
      courses: "Our Courses",
      corporate: "Corporates",
      customerCare: "Customer Care",
      contactUs: "Contact Us",
      blogs: "Blogs",
      followUs: "Follow Us",
      copyright: "© Copyright 2026. Canada English Life. All Rights Reserved.",
      description: "Canada English Life Academy offers comprehensive online English courses for all ages and levels. Learn from native speakers and certified instructors."
    },
    ar: {
      about: "من نحن",
      company: "الشركة",
      aboutUs: "من نحن",
      meetTeam: "تعرف على الفريق",
      services: "خدماتنا",
      courses: "دوراتنا",
      corporate: "الشركات",
      customerCare: "خدمة العملاء",
      contactUs: "اتصل بنا",
      blogs: "المدونات",
      followUs: "تابعنا",
      copyright: "© حقوق الطبع والنشر 2026. Canada English Life. جميع الحقوق محفوظة.",
      description: "تقدم أكاديمية Canada English Life دورات شاملة للغة الإنجليزية عبر الإنترنت لجميع الأعمار والمستويات. تعلم من متحدثين أصليين ومدرسين معتمدين."
    }
  };

  const t = content[language];

  const socialLinks = [
    { icon: Facebook, href: "https://www.facebook.com/CanadaEnglishLifeKSA", label: "Facebook", color: "hover:bg-blue-600" },
    { icon: Instagram, href: "https://www.instagram.com/canada.englishlife/", label: "Instagram", color: "hover:bg-pink-600" },
    { icon: Linkedin, href: "https://www.linkedin.com/company/canadaenglishlife/?viewAsMember=true", label: "LinkedIn", color: "hover:bg-blue-700" },
    { icon: Youtube, href: "https://www.youtube.com/@canadaenglishlife", label: "YouTube", color: "hover:bg-red-600" },
    { icon: MessageCircle, href: "https://wa.me/16478745620", label: "WhatsApp", color: "hover:bg-green-600" }
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_english-academy-lms/artifacts/fhrsr91w_profile_picture.png"
                alt="Canada English Life Academy"
                className="h-12 w-auto object-contain"
              />
              <div>
                <h3 className="text-lg font-bold">Canada English Life</h3>
                <p className="text-xs text-gray-400">{language === 'en' ? 'Since 2017' : 'منذ 2017'}</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {t.description}
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center ${social.color} transition-colors duration-300`}
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">{t.company}</h4>
            <ul className="space-y-3">
              <li>
                <a href="#about" className="text-gray-400 hover:text-teal-400 transition-colors text-sm">
                  {t.aboutUs}
                </a>
              </li>
              <li>
                <a href="#team" className="text-gray-400 hover:text-teal-400 transition-colors text-sm">
                  {t.meetTeam}
                </a>
              </li>
              <li>
                <a href="#blogs" className="text-gray-400 hover:text-teal-400 transition-colors text-sm">
                  {t.blogs}
                </a>
              </li>
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">{t.services}</h4>
            <ul className="space-y-3">
              <li>
                <a href="#courses" className="text-gray-400 hover:text-teal-400 transition-colors text-sm">
                  {t.courses}
                </a>
              </li>
              <li>
                <a href="#corporate" className="text-gray-400 hover:text-teal-400 transition-colors text-sm">
                  {t.corporate}
                </a>
              </li>
              <li>
                <a href="#assessment" className="text-gray-400 hover:text-teal-400 transition-colors text-sm">
                  {language === 'en' ? 'Free Assessment' : 'تقييم مجاني'}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">{t.customerCare}</h4>
            <ul className="space-y-3">
              <li>
                <a href="#contact" className="text-gray-400 hover:text-teal-400 transition-colors text-sm">
                  {t.contactUs}
                </a>
              </li>
              <li>
                <a href="tel:+16479745620" className="text-gray-400 hover:text-teal-400 transition-colors text-sm flex items-center space-x-2">
                  <span>+1 647 974 5620</span>
                </a>
              </li>
              <li>
                <a href="mailto:info@canadaenglishlife.com" className="text-gray-400 hover:text-teal-400 transition-colors text-sm flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>info@canadaenglishlife.com</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm text-center md:text-left">
              {t.copyright}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-teal-400 transition-colors text-sm">
                {language === 'en' ? 'Privacy Policy' : 'سياسة الخصوصية'}
              </Link>
              <Link to="/terms-of-service" className="text-gray-400 hover:text-teal-400 transition-colors text-sm">
                {language === 'en' ? 'Terms of Service' : 'شروط الخدمة'}
              </Link>
              <Link to="/refund-policy" className="text-gray-400 hover:text-teal-400 transition-colors text-sm">
                {language === 'en' ? 'Refund Policy' : 'سياسة الاسترداد'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
